/**
 * useStudioBranding — BRAND-1, variant-aware since BRAND-2.
 *
 * One place to answer: "should this surface render the studio's
 * uploaded logo, or the Orbit fallback?" Consumed by every logo
 * mount inside the signed-in web app:
 *
 *   • the Aperture nav bar  (POL-11a wires the consumer)
 *   • public portal / delivery / gallery headers (when NOT rendering
 *     under a verified custom domain — that path already reads
 *     `useCustomDomainBrand` which owns its own per-domain override)
 *   • the Sites public renderer
 *   • anywhere a studio's own "Orbit" wordmark shows up in-product
 *
 * Three inputs feed the answer:
 *   1. `organizations.brand_logo_dark_url` / `brand_logo_light_url`
 *      / `brand_logo_url` — the per-surface variants (BRAND-2) with
 *      the BRAND-1 single upload as the legacy fallback.
 *   2. `useSubscription().limits.customBranding` — the paid-tier
 *      gate. Core / Pro / Command = true; Free / Lite = false.
 *      Mirrors the flag surfaced in src/services/billing.ts.
 *   3. `useThemeMode()` — which surface the app is currently painting
 *      (dark cosmic shell vs light canvas), so `logoUrl` /
 *      `customLogoUrl` resolve to the variant that actually reads.
 *
 * Variant resolution (also exported as `resolveBrandLogoVariant` for
 * consumers that hold raw columns, e.g. the public portal RPC payload):
 *
 *   dark surface  → brand_logo_dark_url  ?? brand_logo_url
 *   light surface → brand_logo_light_url ?? brand_logo_dark_url
 *                                        ?? brand_logo_url
 *
 * Consumers get `usesCustomLogo` (the composed answer) plus the
 * raw pieces so they can decide when to show an upsell hint.
 *
 * The fallback URL comes from `FALLBACK_ORBIT_LOGO` — no hard-coded
 * imports so a POL-11 asset swap doesn't require touching every
 * consumer.
 */

import { useOrganization } from '@/services/organizations';
import { useSubscription } from '@/hooks/useSubscription';
import { useThemeMode } from '@/hooks/useThemeMode';

/**
 * Path to the standard Orbit wordmark asset. Kept centralized so a
 * one-line swap here retunes every surface that falls back. When
 * POL-11a ships an SVG mark this constant becomes the source of
 * truth for the Aperture bar's default too.
 */
export const FALLBACK_ORBIT_LOGO = '/orbit-wordmark.svg';

/** Which background the logo is about to sit on. */
export type BrandLogoSurface = 'dark' | 'light';

/**
 * Resolve which uploaded mark should render on a given surface.
 * Pure — shared by the signed-in hook below AND public consumers
 * (portal RPC payloads carry the same three raw fields as
 * `logo_dark_url` / `logo_light_url` / `logo_url`).
 *
 *   dark  → dark ?? legacy
 *   light → light ?? dark ?? legacy
 *
 * The light chain deliberately passes through the dark variant
 * before legacy: a studio that only uploaded a dark-surface mark
 * still gets *their* logo (possibly imperfect contrast) rather
 * than silently reverting to Orbit branding on light surfaces.
 */
export function resolveBrandLogoVariant(
  surface: BrandLogoSurface,
  variants: {
    dark?: string | null;
    light?: string | null;
    legacy?: string | null;
  },
): string | null {
  const dark = variants.dark || null;
  const light = variants.light || null;
  const legacy = variants.legacy || null;
  return surface === 'dark' ? dark ?? legacy : light ?? dark ?? legacy;
}

export interface StudioBrandingResult {
  /**
   * Effective logo URL to render — the studio's mark if `usesCustomLogo`
   * is true, otherwise the standard Orbit wordmark. Never null; every
   * consumer can just `<img src={logoUrl} />` without a conditional.
   * Theme-aware since BRAND-2: resolves the variant for the current
   * `useThemeMode()` surface.
   */
  logoUrl: string;
  /**
   * True iff paid-tier gate PASSED and a logo is set (any variant).
   * Consumers gate upsell UI on the inverse.
   */
  usesCustomLogo: boolean;
  /**
   * The custom logo URL the studio uploaded, or null. Kept separate
   * from `logoUrl` so consumers building a preview of "what would
   * unlock" (upgrade panel) can show the uploaded mark even when
   * the paid gate is false. Theme-aware since BRAND-2 — resolves via
   * `resolveBrandLogoVariant` for the current mode, so every existing
   * consumer became variant-correct without a code change.
   */
  customLogoUrl: string | null;
  /** Raw `brand_logo_dark_url` column — dark-surface variant, or null. */
  customLogoDarkUrl: string | null;
  /** Raw `brand_logo_light_url` column — light-surface variant, or null. */
  customLogoLightUrl: string | null;
  /** Raw legacy `brand_logo_url` column (BRAND-1 single upload), or null. */
  legacyLogoUrl: string | null;
  /**
   * Variant-aware accessor for surfaces whose background does NOT
   * follow the app theme (e.g. an always-dark Aperture pill inside a
   * light-mode page, or a light email preview inside the dark shell).
   * Returns null when nothing is uploaded — the paid gate is NOT
   * applied here (mirrors `customLogoUrl` semantics).
   */
  logoForSurface: (surface: BrandLogoSurface) => string | null;
  /** Always the constant fallback. Convenience alias for readability. */
  fallbackLogo: string;
  /**
   * True iff the plan tier supports custom branding at all (Core+).
   * Consumers use this to decide whether to show an upgrade nudge.
   */
  canUseCustomLogo: boolean;
  isLoading: boolean;
}

export function useStudioBranding(): StudioBrandingResult {
  const { data: org, isLoading: orgLoading } = useOrganization();
  const { limits, isLoading: subLoading } = useSubscription();
  const { mode } = useThemeMode();

  const customLogoDarkUrl = org?.brand_logo_dark_url || null;
  const customLogoLightUrl = org?.brand_logo_light_url || null;
  const legacyLogoUrl = org?.brand_logo_url || null;

  const logoForSurface = (surface: BrandLogoSurface): string | null =>
    resolveBrandLogoVariant(surface, {
      dark: customLogoDarkUrl,
      light: customLogoLightUrl,
      legacy: legacyLogoUrl,
    });

  // Theme-resolved mark — the one the current shell should paint.
  const customLogoUrl = logoForSurface(mode);

  const canUseCustomLogo = !!limits?.customBranding;
  const usesCustomLogo = canUseCustomLogo && !!customLogoUrl;

  return {
    logoUrl: usesCustomLogo ? customLogoUrl! : FALLBACK_ORBIT_LOGO,
    usesCustomLogo,
    customLogoUrl,
    customLogoDarkUrl,
    customLogoLightUrl,
    legacyLogoUrl,
    logoForSurface,
    fallbackLogo: FALLBACK_ORBIT_LOGO,
    canUseCustomLogo,
    isLoading: orgLoading || subLoading,
  };
}
